import { CommonModule, formatDate } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import * as XLSX from 'xlsx';
import { MenuItem, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { DrawerModule } from 'primeng/drawer';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { ChartOfAccounts } from '../../../chart-of-accounts/chart-of-accounts.model';
import { ChartOfAccountsService } from '../../../chart-of-accounts/chart-of-accounts.service';
import { app_permissions } from '../../../security/app-permissions';
import { PermissionService } from '../../../security/permission.service';
import { SessionStorageService } from '../../../services/session-storage.service';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageSearchPanelComponent } from '../../../shared/page-layout/page-search-panel.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { PageMetric } from '../../../shared/page-layout/page-layout.models';
import { BulkCost, BulkCostLine, BulkCostSourceCandidate, BulkCostSummary } from './bulk-cost.model';
import { BulkCostService } from './bulk-cost.service';
import { accountingNumberWidth } from '../../shared/accounting-line-width';

@Component({
  selector: 'app-bulk-costs',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ToastModule,
    ProgressBarModule,
    ButtonModule,
    CheckboxModule,
    DatePickerModule,
    DialogModule,
    DrawerModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    InputTextModule,
    MultiSelectModule,
    SelectModule,
    TableModule,
    TagModule,
    TextareaModule,
    TooltipModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageSearchPanelComponent,
    PageDataPanelComponent
  ],
  providers: [MessageService],
  templateUrl: './bulk-costs.component.html',
  styleUrls: ['../../finance-list.shared.css', './bulk-costs.component.css']
})
export class BulkCostsComponent implements OnInit {
  readonly numberInputWidth = accountingNumberWidth;
  breadcrumbItems: MenuItem[] = [{ label: 'Accounts' }, { label: 'Enterprise' }, { label: 'Payables' }, { label: 'Bulk Costs' }];
  rows: BulkCost[] = [];
  selectedRows: BulkCost[] = [];
  preview?: BulkCost;
  editing: BulkCost = { currencyCode: 'SAR', exchangeRate: 1, lines: [] };
  sourceCandidates: BulkCostSourceCandidate[] = [];
  selectedSourceCandidates: BulkCostSourceCandidate[] = [];
  accounts: ChartOfAccounts[] = [];
  isLoading = false;
  isExporting = false;
  isSaving = false;
  isSourceLoading = false;
  editorVisible = false;
  previewVisible = false;
  sourceDialogVisible = false;
  totalRecords = 0;
  first = 0;
  pageSize = 25;
  summary: BulkCostSummary = {};
  keyword = '';
  dateRange: (Date | null)[] | null = null;
  selectedStatuses: string[] = ['UNAPPROVED', 'APPROVED'];
  selectedPostingStatuses: string[] = [];
  statusOptions = ['UNAPPROVED', 'APPROVED', 'POSTED', 'CANCELLED', 'EXCEPTION'];
  postingStatusOptions = ['NOT_POSTED', 'POSTED', 'CANCELLED'];
  sourceTypeOptions = [
    'MANUAL',
    'SHIPMENT_INTERNAL_COST',
    'SHIPMENT',
    'DELIVERY',
    'SALES_ORDER',
    'ORDER_RELEASE',
    'CLEARANCE',
    'WAREHOUSING',
    'PROJECT'
  ];

  get canView(): boolean {
    return this.permissions.hasPermission(app_permissions.accounts.bill.view);
  }

  get canExport(): boolean {
    return this.canView;
  }

  get canEdit(): boolean {
    return this.permissions.hasPermission(app_permissions.accounts.bill.edit);
  }

  get canPost(): boolean {
    return this.permissions.hasPermission(app_permissions.accounts.bill.post);
  }

  get metrics(): PageMetric[] {
    const currency = this.summary.currencyCode || this.editing.currencyCode || 'SAR';
    return [
      { label: 'Unapproved', value: this.formatMoney(this.summary.unapprovedAmount, currency), hint: `${this.summary.unapprovedCount || 0} bulk costs`, tone: 'warn' },
      { label: 'Approved', value: this.formatMoney(this.summary.approvedAmount, currency), hint: `${this.summary.approvedCount || 0} ready to post`, tone: 'success' },
      { label: 'Posted', value: this.formatMoney(this.summary.postedAmount, currency), hint: `${this.summary.postedCount || 0} posted`, tone: 'info' },
      { label: 'Selected', value: this.formatMoney(this.selectedTotal(), currency), hint: `${this.selectedRows.length} selected`, tone: 'default' }
    ];
  }

  get activeFilterCount(): number {
    return [
      this.keyword ? 1 : 0,
      this.dateRange?.some(Boolean) ? 1 : 0,
      this.selectedStatuses.length,
      this.selectedPostingStatuses.length
    ].reduce((total, value) => total + Number(value), 0);
  }

  get resultText(): string {
    return `${this.totalRecords} bulk costs`;
  }

  constructor(
    private readonly service: BulkCostService,
    private readonly session: SessionStorageService,
    private readonly permissions: PermissionService,
    private readonly messageService: MessageService,
    private readonly chartOfAccountsService: ChartOfAccountsService,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.editing = this.newBulkCost();
    if (!this.canView) {
      return;
    }
    this.loadAccounts();
    this.loadSummary();
    this.applyFilters();
  }

  loadData(event: TableLazyLoadEvent): void {
    this.first = event.first ?? 0;
    this.pageSize = event.rows ?? this.pageSize;
    this.applyFilters(false);
  }

  applyFilters(resetPage = true): void {
    if (resetPage) {
      this.first = 0;
    }
    this.isLoading = true;
    this.service.list({
      owningCompanyId: this.companyId(),
      statuses: this.selectedStatuses,
      postingStatuses: this.selectedPostingStatuses,
      startDate: this.apiDate(this.dateRange?.[0]),
      endDate: this.apiDate(this.dateRange?.[1]),
      keyword: this.keyword,
      page: Math.floor(this.first / this.pageSize),
      pageSize: this.pageSize
    }).subscribe({
      next: response => {
        this.rows = response.items || [];
        this.totalRecords = response.count || 0;
        this.selectedRows = [];
        this.isLoading = false;
        this.loadSummary();
      },
      error: err => {
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Bulk costs', detail: err?.error?.message || 'Could not load bulk costs.' });
      }
    });
  }

  clearFilters(): void {
    this.keyword = '';
    this.dateRange = null;
    this.selectedStatuses = ['UNAPPROVED', 'APPROVED'];
    this.selectedPostingStatuses = [];
    this.applyFilters();
  }

  openCreate(): void {
    this.router.navigate(['/accounts/enterprise/payables/bulk-costs/new']);
  }

  openAccountDefaults(): void {
    this.router.navigate(['/accounts/enterprise/payables/bulk-costs/account-defaults']);
  }

  openSourceImport(): void {
    const companyId = this.companyId();
    if (!companyId) {
      this.messageService.add({ severity: 'warn', summary: 'Source costs', detail: 'Select a company before importing source costs.' });
      return;
    }
    this.sourceDialogVisible = true;
    this.selectedSourceCandidates = [];
    this.loadSourceCandidates();
  }

  loadSourceCandidates(): void {
    const companyId = this.companyId();
    if (!companyId) return;
    this.isSourceLoading = true;
    this.service.sourceCandidates(companyId).subscribe({
      next: candidates => {
        this.sourceCandidates = candidates || [];
        this.isSourceLoading = false;
      },
      error: err => {
        this.isSourceLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Source costs', detail: err?.error?.message || 'Could not load source cost rows.' });
      }
    });
  }

  createFromSources(): void {
    const allocationIds = this.selectedSourceCandidates
      .map(row => row.allocationId || row.sourceDetailId)
      .filter((id): id is number => !!id);
    if (!allocationIds.length) {
      this.messageService.add({ severity: 'warn', summary: 'Source costs', detail: 'Select at least one source row.' });
      return;
    }
    this.isSaving = true;
    this.service.importFromSources({
      owningCompanyId: this.companyId(),
      sourceType: 'SHIPMENT_INTERNAL_COST',
      supplierName: 'Internal Transport Cost',
      invoiceDate: new Date(),
      invoiceReceivedDate: new Date(),
      postingDate: new Date(),
      currencyCode: this.selectedSourceCandidates[0]?.currencyCode || 'SAR',
      allocationIds
    }).subscribe({
      next: saved => {
        this.isSaving = false;
        this.sourceDialogVisible = false;
        this.messageService.add({ severity: 'success', summary: 'Bulk cost', detail: `${saved.bulkCostNumber || 'Bulk cost'} created from source rows.` });
        if (saved.bulkCostId) {
          this.router.navigate(['/accounts/enterprise/payables/bulk-costs', saved.bulkCostId, 'edit']);
        } else {
          this.applyFilters(false);
        }
      },
      error: err => {
        this.isSaving = false;
        this.messageService.add({ severity: 'error', summary: 'Import failed', detail: err?.error?.message || 'Could not create bulk cost from source rows.' });
      }
    });
  }

  openEdit(row: BulkCost): void {
    if (!row.bulkCostId) {
      return;
    }
    this.router.navigate(['/accounts/enterprise/payables/bulk-costs', row.bulkCostId, 'edit']);
  }

  openView(row: BulkCost): void {
    if (!row.bulkCostId) {
      return;
    }
    this.router.navigate(['/accounts/enterprise/payables/bulk-costs', row.bulkCostId]);
  }

  openPreview(row: BulkCost): void {
    if (!row.bulkCostId) {
      return;
    }
    this.service.get(row.bulkCostId).subscribe(cost => {
      this.preview = cost;
      this.previewVisible = true;
    });
  }

  save(): void {
    if (!this.editing.owningCompanyId) {
      this.editing.owningCompanyId = this.companyId();
    }
    this.editing.lines = (this.editing.lines || []).filter(line => this.lineAmount(line) > 0 || !!line.jobReference);
    if (!this.editing.lines.length) {
      this.messageService.add({ severity: 'warn', summary: 'Bulk cost', detail: 'Add at least one cost line.' });
      return;
    }
    this.editing.grossAmount = this.totalAmount();
    this.editing.taxAmount = this.totalTax();
    this.editing.localAmount = this.totalLocal();
    this.isSaving = true;
    this.service.save(this.serialize(this.editing)).subscribe({
      next: saved => {
        this.isSaving = false;
        this.editorVisible = false;
        this.messageService.add({ severity: 'success', summary: 'Bulk cost', detail: `${saved.bulkCostNumber || 'Bulk cost'} saved.` });
        this.applyFilters(false);
      },
      error: err => {
        this.isSaving = false;
        this.messageService.add({ severity: 'error', summary: 'Save failed', detail: err?.error?.message || 'Could not save bulk cost.' });
      }
    });
  }

  approve(row: BulkCost): void {
    if (!row.bulkCostId) return;
    this.service.approve(row.bulkCostId, this.actionRequest()).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Approved', detail: 'Bulk cost approved.' });
        this.applyFilters(false);
      },
      error: err => this.messageService.add({ severity: 'error', summary: 'Approve failed', detail: err?.error?.message || 'Could not approve bulk cost.' })
    });
  }

  post(row: BulkCost): void {
    if (!row.bulkCostId) return;
    const payableAccountId = row.payableAccountId || this.editing.payableAccountId;
    this.service.post(row.bulkCostId, { ...this.actionRequest(), payableAccountId }).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Posted', detail: 'Bulk cost posted to journal entry.' });
        this.applyFilters(false);
      },
      error: err => this.messageService.add({ severity: 'error', summary: 'Post failed', detail: err?.error?.message || 'Posting requires payable and line debit accounts.' })
    });
  }

  cancel(row: BulkCost): void {
    if (!row.bulkCostId) return;
    this.service.cancel(row.bulkCostId, this.actionRequest()).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Cancelled', detail: 'Bulk cost cancelled.' });
        this.applyFilters(false);
      },
      error: err => this.messageService.add({ severity: 'error', summary: 'Cancel failed', detail: err?.error?.message || 'Could not cancel bulk cost.' })
    });
  }

  bulkApprove(): void {
    const rows = this.selectedRows.filter(row => row.bulkCostId && row.status !== 'POSTED' && row.status !== 'CANCELLED');
    if (!rows.length) return;
    this.isLoading = true;
    forkJoin(rows.map(row => this.service.approve(row.bulkCostId!, this.actionRequest()))).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Approved', detail: `${rows.length} bulk costs approved.` });
        this.applyFilters(false);
      },
      error: err => {
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Bulk approve failed', detail: err?.error?.message || 'Could not approve selected rows.' });
      }
    });
  }

  addLine(): void {
    this.editing.lines = [...(this.editing.lines || []), this.newLine()];
  }

  removeLine(index: number): void {
    this.editing.lines = (this.editing.lines || []).filter((_, rowIndex) => rowIndex !== index);
  }

  duplicateLine(line: BulkCostLine): void {
    const copy = { ...line, bulkCostLineId: undefined };
    this.editing.lines = [...(this.editing.lines || []), copy];
  }

  lineAmount(line: BulkCostLine): number {
    const amount = Number(line.amount || 0);
    if (amount) return amount;
    return Number(line.quantity || 0) * Number(line.price || 0);
  }

  lineTotal(line: BulkCostLine): number {
    return this.lineAmount(line) + (this.editing.includeTax ? Number(line.taxAmount || 0) : 0);
  }

  totalAmount(): number {
    return (this.editing.lines || []).reduce((sum, line) => sum + this.lineAmount(line), 0);
  }

  totalTax(): number {
    return (this.editing.lines || []).reduce((sum, line) => sum + Number(line.taxAmount || 0), 0);
  }

  totalLocal(): number {
    const exchange = Number(this.editing.exchangeRate || 1);
    return (this.editing.lines || []).reduce((sum, line) => sum + this.lineTotal(line), 0) * exchange;
  }

  selectedTotal(): number {
    return this.selectedRows.reduce((sum, row) => sum + Number(row.localAmount || row.grossAmount || 0), 0);
  }

  sourceSelectedTotal(): number {
    return this.selectedSourceCandidates.reduce((sum, row) => sum + Number(row.localAmount || row.amount || 0), 0);
  }

  sourceSelectedCurrency(): string {
    return this.selectedSourceCandidates.length ? this.selectedSourceCandidates[0].currencyCode || 'SAR' : 'SAR';
  }

  formatMoney(value?: number, currency = 'SAR'): string {
    return `${currency || ''} ${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`;
  }

  statusSeverity(status?: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' {
    if (status === 'POSTED') return 'success';
    if (status === 'APPROVED') return 'info';
    if (status === 'UNAPPROVED') return 'warn';
    if (status === 'CANCELLED' || status === 'EXCEPTION') return 'danger';
    return 'secondary';
  }

  viewSource(line: BulkCostLine): void {
    if (line.shipmentId) {
      this.router.navigate(['/shipment/view', line.shipmentId]);
    } else if (line.deliveryId) {
      this.router.navigate(['/shipment/delivery/view', line.deliveryId]);
    }
  }

  private loadAccounts(): void {
    const companyId = this.companyId();
    if (!companyId) return;
    this.chartOfAccountsService.getAllChartOfAccountsByCompany(companyId, false).subscribe(accounts => {
      this.accounts = accounts || [];
    });
  }

  private loadSummary(): void {
    this.service.summary(this.companyId(), this.apiDate(this.dateRange?.[0]), this.apiDate(this.dateRange?.[1]))
      .subscribe(summary => this.summary = summary || {});
  }

  private newBulkCost(): BulkCost {
    return {
      owningCompanyId: this.companyId(),
      sourceType: 'MANUAL',
      status: 'UNAPPROVED',
      approvalStatus: 'PENDING',
      postingStatus: 'NOT_POSTED',
      currencyCode: 'SAR',
      exchangeRate: 1,
      invoiceDate: new Date(),
      invoiceReceivedDate: new Date(),
      postingDate: new Date(),
      includeTax: false,
      lines: [this.newLine()]
    };
  }

  private newLine(): BulkCostLine {
    return {
      sourceType: 'MANUAL',
      description: 'Transportation',
      unit: 'Trip',
      quantity: 1,
      price: 0,
      taxAmount: 0,
      amount: 0,
      status: 'UNAPPROVED'
    };
  }

  private prepareForEdit(cost: BulkCost): BulkCost {
    return {
      ...cost,
      invoiceDate: this.toDate(cost.invoiceDate),
      invoiceReceivedDate: this.toDate(cost.invoiceReceivedDate),
      postingDate: this.toDate(cost.postingDate),
      lines: cost.lines?.length ? cost.lines : [this.newLine()]
    };
  }

  projectContextLabel(cost?: BulkCost, fallbackForMultiple = 'Multiple projects on cost lines'): string {
    if (cost?.logisticsProjectNumber) {
      return cost.logisticsProjectNumber;
    }
    const hasLineProjects = (cost?.lines || []).some(line => !!line.logisticsProjectNumber || !!line.logisticsProjectId);
    return hasLineProjects ? fallbackForMultiple : 'Not assigned';
  }

  private serialize(cost: BulkCost): BulkCost {
    return {
      ...cost,
      invoiceDate: this.apiDate(cost.invoiceDate),
      invoiceReceivedDate: this.apiDate(cost.invoiceReceivedDate),
      postingDate: this.apiDate(cost.postingDate),
      lines: (cost.lines || []).map(line => ({
        ...line,
        amount: this.lineAmount(line),
        localAmount: this.lineTotal(line) * Number(cost.exchangeRate || 1)
      }))
    };
  }

  private actionRequest() {
    return {
      actorCustomerId: this.session.getCustomer()?.id,
      actionDate: new Date()
    };
  }

  private companyId(): number | undefined {
    return this.session.getCompany()?.id;
  }

  private apiDate(value?: Date | string | null): string | undefined {
    if (!value) return undefined;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
  }

  exportToExcel(): void {
    if (!this.canExport) {
      this.messageService.add({ severity: 'warn', summary: 'Permission', detail: 'You do not have permission to export bulk costs.' });
      return;
    }

    if (this.selectedRows.length > 0) {
      this.generateExcelFile(this.selectedRows);
      return;
    }

    if (this.totalRecords === 0 || !this.rows.length) {
      this.messageService.add({ severity: 'warn', summary: 'No Data', detail: 'There is no bulk cost data to export.' });
      return;
    }

    if (this.totalRecords <= this.rows.length) {
      this.generateExcelFile(this.rows);
      return;
    }

    this.isExporting = true;
    const maxExportLimit = Math.min(this.totalRecords, 5000);
    this.service.list({
      owningCompanyId: this.companyId(),
      statuses: this.selectedStatuses,
      postingStatuses: this.selectedPostingStatuses,
      startDate: this.apiDate(this.dateRange?.[0]),
      endDate: this.apiDate(this.dateRange?.[1]),
      keyword: this.keyword,
      page: 0,
      pageSize: maxExportLimit
    }).subscribe({
      next: response => {
        this.isExporting = false;
        const items = response.items || [];
        if (!items.length) {
          this.messageService.add({ severity: 'warn', summary: 'No Data', detail: 'There is no bulk cost data to export.' });
          return;
        }
        this.generateExcelFile(items);
      },
      error: err => {
        this.isExporting = false;
        this.messageService.add({ severity: 'error', summary: 'Export failed', detail: err?.error?.message || 'Could not load bulk costs for export.' });
      }
    });
  }

  private generateExcelFile(rows: BulkCost[]): void {
    const exportRows = rows.map(row => ({
      'Row No': row.bulkCostNumber || ('BC-' + row.bulkCostId),
      'Supplier / Source': row.supplierName || 'Internal / Source Cost',
      'Source Type': row.sourceType || 'MANUAL',
      'Delivery Number': row.deliveryNumber || row.shipmentNumber || '',
      'Project': row.logisticsProjectNumber || '',
      'Driver': row.driverName || '',
      'Vehicle': row.vehicleLabel || '',
      'Currency': row.currencyCode || 'SAR',
      'Exchange Rate': row.exchangeRate ?? 1,
      'Invoice No': row.invoiceNumber || '',
      'Invoice Date': this.formatExportDate(row.invoiceDate),
      'Invoice Received Date': this.formatExportDate(row.invoiceReceivedDate),
      'Posting Date': this.formatExportDate(row.postingDate),
      'Excl. Tax': row.grossAmount ?? 0,
      'VAT': row.taxAmount ?? 0,
      'Net Amount': row.netAmount ?? 0,
      'Local Amount': row.localAmount ?? 0,
      'Status': row.status || 'UNAPPROVED',
      'Approval Status': row.approvalStatus || 'PENDING',
      'Approved At': this.formatExportDateTime(row.approvedAt),
      'Posting Status': row.postingStatus || 'NOT_POSTED',
      'Posted At': this.formatExportDateTime(row.postedAt),
      'Journal Entry #': row.journalEntryId ?? '',
      'Notes': row.notes || ''
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    worksheet['!cols'] = Object.keys(exportRows[0]).map(() => ({ wch: 20 }));
    const workbook = { Sheets: { 'Bulk Costs': worksheet }, SheetNames: ['Bulk Costs'] };
    const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    this.saveAsExcelFile(excelBuffer, `bulk-costs_${this.exportDateStamp()}`);

    const isSelectedOnly = this.selectedRows.length > 0 && rows === this.selectedRows;
    this.messageService.add({
      severity: 'success',
      summary: 'Export Complete',
      detail: isSelectedOnly
        ? `Exported ${rows.length} selected bulk cost${rows.length === 1 ? '' : 's'}.`
        : `Exported ${rows.length} bulk cost${rows.length === 1 ? '' : 's'}.`
    });
  }

  private saveAsExcelFile(buffer: any, fileName: string): void {
    const data: Blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
    });
    const url = window.URL.createObjectURL(data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileName}.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  private formatExportDate(value?: Date | string | null): string {
    if (!value) return '';
    try {
      return formatDate(value, 'yyyy-MM-dd', 'en-US');
    } catch {
      return '';
    }
  }

  private formatExportDateTime(value?: Date | string | null): string {
    if (!value) return '';
    try {
      return formatDate(value, 'yyyy-MM-dd HH:mm', 'en-US');
    } catch {
      return '';
    }
  }

  private exportDateStamp(): string {
    return formatDate(new Date(), 'yyyyMMdd_HHmmss', 'en-US');
  }

  private toDate(value?: Date | string | null): Date | undefined {
    if (!value) return undefined;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date;
  }
}
